<template>
	<v-dialog v-model="isVisible" max-width="560" persistent>
		<v-card>
			<v-card-title class="headline">
				{{ $t('label.successfactors.dialog-title') }}
			</v-card-title>

			<v-card-text style="padding: 0">
				<v-alert v-if="errorMessage" type="error" dense class="mx-4 mt-4">
					{{ errorMessage }}
				</v-alert>
				<v-alert v-if="isLoading" type="info" dense class="mx-4 mt-4" icon="cloud_upload">
					{{ $t('label.successfactors.sending') }}
				</v-alert>
				<v-form ref="form">
					<v-container fluid>
						<v-row>
							<v-col>
								<v-text-field
									v-model="form.fullName"
									:label="`${$t('label.successfactors.full-name')}*`"
									:rules="[v => !!v || $t('label.successfactors.required')]"
									outlined
									dense
									hide-details="auto"
								/>
							</v-col>
							<v-col>
								<v-text-field
									v-model="form.email"
									:label="`${$t('label.successfactors.email')}*`"
									:rules="[v => !!v || $t('label.successfactors.required')]"
									outlined
									dense
									hide-details="auto"
								/>
							</v-col>
						</v-row>
						<v-row>
							<v-col>
								<v-text-field
									v-model="form.phoneNumber"
									:label="`${$t('label.successfactors.phone')}*`"
									:rules="[
										v => !!v || $t('label.successfactors.required'),
										v => /^05\d{9}$/.test(v) || $t('label.successfactors.phone-format'),
									]"
									outlined
									dense
									hide-details="auto"
									placeholder="05xxxxxxxxx"
								/>
							</v-col>
						</v-row>
						<v-row>
							<v-col>
								<DatePicker
									v-model="form.dateOfBirth"
									:label="`${$t('label.successfactors.date-of-birth')}*`"
									:errors="!form.dateOfBirth && submitted ? [$t('label.successfactors.required')] : []"
									dateFormat="dd.MM.yyyy"
									outlined
									dense
								/>
							</v-col>
							<v-col>
								<DatePicker
									v-model="form.startDate"
									:label="`${$t('label.successfactors.start-date')}*`"
									:errors="!form.startDate && submitted ? [$t('label.successfactors.required')] : []"
									dateFormat="dd.MM.yyyy"
									outlined
									dense
								/>
							</v-col>
						</v-row>
						<v-row>
							<v-col>
								<v-autocomplete
									v-model="form.placeOfBirth"
									:label="`${$t('label.successfactors.place-of-birth')}*`"
									:items="placeOfBirthOptions"
									:rules="[v => !!v || $t('label.successfactors.required')]"
									outlined
									dense
									hide-details="auto"
								/>
							</v-col>
							<v-col>
								<v-autocomplete
									v-model="form.countryOfBirth"
									:label="`${$t('label.successfactors.country-of-birth')}*`"
									:items="countryOptions"
									:rules="[v => !!v || $t('label.successfactors.required')]"
									outlined
									dense
									hide-details="auto"
								/>
							</v-col>
						</v-row>
						<v-row>
							<v-col>
								<v-autocomplete
									v-model="form.nationality"
									:label="`${$t('label.successfactors.nationality')}*`"
									:items="countryOptions"
									:rules="[v => !!v || $t('label.successfactors.required')]"
									outlined
									dense
									hide-details="auto"
								/>
							</v-col>
							<v-col>
								<v-select
									v-model="form.gender"
									:label="`${$t('label.successfactors.gender')}*`"
									:items="genderOptions"
									:rules="[v => !!v || $t('label.successfactors.required')]"
									outlined
									dense
									hide-details="auto"
								/>
							</v-col>
						</v-row>
					</v-container>
				</v-form>
			</v-card-text>

			<v-card-actions>
				<v-spacer />
				<v-btn text @click="handleClose" :disabled="isLoading">
					{{ $t('button.cancel') }}
				</v-btn>
				<v-btn
					color="primary"
					@click="handleSubmit"
					:loading="isLoading"
					:disabled="isLoading"
				>
					{{ $t('label.successfactors.send') }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import axios from 'axios';
import DatePicker from '@/components/shared/datePicker/DatePicker.vue';
import { SF_COUNTRY_OPTIONS, SF_PROVINCE_OPTIONS } from './sf-options';

export default {
	name: 'SuccessFactorsDialog',

	components: {
		DatePicker,
	},

	props: {
		isVisible: {
			type: Boolean,
			default: false,
		},
		candidate: {
			type: Object,
			default: null,
		},
	},

	data() {
		return {
			isLoading: false,
			submitted: false,
			errorMessage: null,
			form: {
				fullName: '',
				email: '',
				phoneNumber: '',
				dateOfBirth: '',
				startDate: '',
				placeOfBirth: '',
				countryOfBirth: '',
				nationality: '',
				gender: null,
			},
			genderOptions: [
				{ text: this.$t('label.successfactors.gender-male'), value: 'M' },
				{ text: this.$t('label.successfactors.gender-female'), value: 'F' },
			],
			countryOptions: SF_COUNTRY_OPTIONS,
			placeOfBirthOptions: SF_PROVINCE_OPTIONS,
		};
	},

	watch: {
		isVisible(val) {
			if (val && this.candidate) {
				this.prefillForm();
			}
		},
	},

	methods: {
		prefillForm() {
			const c = this.candidate;
			this.form = {
				fullName: c.name || '',
				email: Array.isArray(c.email) ? c.email[0] : c.email || '',
				phoneNumber: Array.isArray(c.phone) ? c.phone[0] : c.phone || '',
				dateOfBirth: c.birth_date ? c.birth_date.split('T')[0] : '',
				startDate: '',
				placeOfBirth: c.city || null,
				countryOfBirth: 'TUR',
				nationality: 'TUR',
				gender: null,
			};
		},

		async handleSubmit() {
			this.submitted = true;
			const isValid = await this.$refs.form.validate();
			if (!isValid || !this.form.startDate || !this.form.dateOfBirth) return;

			this.isLoading = true;
			this.errorMessage = null;
			try {
				const payload = {
					...this.form,
					// YYYY-MM-DD only — no time, no timezone
					dateOfBirth: this.form.dateOfBirth.split('T')[0],
					// YYYY-MM-DDTHH:mm:ss — with time but no Z
					startDate: `${this.form.startDate.split('T')[0]}T00:00:00`,
				};
				await axios.post(
					`/v1/hire-app/successfactors/hiree/${this.candidate.id}`,
					payload,
				);
				this.$emit('success');
				this.handleClose();
			} catch (error) {
				this.errorMessage =
					error.response?.data?.message || error.message || this.$t('label.successfactors.error-message');
				this.$emit('error', this.errorMessage);
			} finally {
				this.isLoading = false;
			}
		},

		handleClose() {
			this.submitted = false;
			this.errorMessage = null;
			this.$refs.form && this.$refs.form.reset();
			this.$emit('update:isVisible', false);
		},
	},
};
</script>
